import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import {
  BREAKPOINTS,
  useMediaQuery,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import classNames from 'classnames';
import { GLOBAL } from 'saddlebag-browser';

import {
  setStickySearchControlsBounds,
  setStickySearchControlsHeight,
} from '../../hooks/useStickySearchControlsHeight';

import STYLES from './SearchControlsWrapper.module.scss';

const $window = GLOBAL.getWindow();

type StickySearchControlsProps = {
  children: ReactNode;
  enableDesktopSticky?: boolean;
  enableMobileSticky?: boolean;
  /** Called when the controls enter the fixed layout. */
  onStick?: (container: HTMLDivElement) => void;
  className?: string;
  'data-testid'?: string;
};

/**
 * On enabled viewport sizes, fixes its children to the viewport once the user has scrolled
 * past it, while leaving everything else on the page (e.g. the hero
 * title/background rendered above it by SearchControlsWrapper) scrolling
 * normally. A placeholder keeps the page height stable so content below
 * doesn't jump when the fixed state toggles.
 */
const StickySearchControls = ({
  children,
  className = '',
  enableDesktopSticky = false,
  enableMobileSticky = false,
  onStick = undefined,
  ...rest
}: StickySearchControlsProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.ABOVE_TABLET);
  const enabled = isDesktop ? enableDesktopSticky : enableMobileSticky;
  const placeholderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setSticky] = useState(false);
  const [placeholderHeight, setPlaceholderHeight] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    if (isSticky && containerRef.current) {
      onStick?.(containerRef.current);
    }
  }, [isSticky, onStick]);

  useEffect(() => {
    // Measure either layout on the existing form, restoring its styles before paint.
    const measureHeight = (layout: 'fixed' | 'flow') => {
      const placeholder = placeholderRef.current;
      const container = containerRef.current;
      const inner = innerRef.current;
      if (!placeholder || !container || !inner) return 0;

      const fixed = layout === 'fixed';
      const wasFixed = container.classList.contains(
        STYLES.StickySearchControls__fixed,
      );
      if (fixed && wasFixed) {
        return container.getBoundingClientRect().height;
      }

      const wasInnerFixed = inner.classList.contains(
        STYLES.StickySearchControls__inner,
      );
      const previousHeight = placeholder.style.height;
      container.classList.toggle(STYLES.StickySearchControls__fixed, fixed);
      inner.classList.toggle(STYLES.StickySearchControls__inner, fixed);
      if (!fixed) placeholder.style.height = '';

      try {
        return (fixed ? container : placeholder).getBoundingClientRect().height;
      } finally {
        container.classList.toggle(
          STYLES.StickySearchControls__fixed,
          wasFixed,
        );
        inner.classList.toggle(
          STYLES.StickySearchControls__inner,
          wasInnerFixed,
        );
        placeholder.style.height = previousHeight;
      }
    };
    const measureFixedHeight = () => measureHeight('fixed');

    const updatePosition = () => {
      if (!placeholderRef.current || !containerRef.current) return;

      const { height, top } = placeholderRef.current.getBoundingClientRect();
      const shouldStick = enabled && top <= 0;
      setStickySearchControlsBounds(
        enabled
          ? { top: top + $window.pageYOffset, height: measureFixedHeight }
          : undefined,
      );
      // Keep the normal-flow footprint (including margins) when fixed sizing
      // changes. Otherwise a centred hero can move the activation threshold.
      if (
        !containerRef.current.classList.contains(
          STYLES.StickySearchControls__fixed,
        )
      ) {
        setPlaceholderHeight(height);
      }
      setSticky(shouldStick);
      setStickySearchControlsHeight(shouldStick ? measureFixedHeight() : 0);
    };

    const updateLayout = () => {
      const placeholder = placeholderRef.current;
      const container = containerRef.current;
      if (!placeholder || !container) return;

      const height = measureHeight('flow');
      // Apply the footprint before reading top: centred heroes can move the anchor.
      if (container.classList.contains(STYLES.StickySearchControls__fixed)) {
        placeholder.style.height = `${height}px`;
      }
      setPlaceholderHeight(height);
      updatePosition();
    };

    updateLayout();
    // Search controls can change height after lazy loading or changing layout.
    const observer = new ResizeObserver(updateLayout);
    if (containerRef.current) observer.observe(containerRef.current);

    $window.addEventListener('scroll', updatePosition);
    $window.addEventListener('resize', updateLayout);

    return () => {
      observer.disconnect();
      $window.removeEventListener('scroll', updatePosition);
      $window.removeEventListener('resize', updateLayout);
      setStickySearchControlsHeight(0);
      setStickySearchControlsBounds(undefined);
    };
  }, [enabled, isDesktop]);

  return (
    <div
      ref={placeholderRef}
      className={STYLES.StickySearchControls__placeholder}
      style={isSticky ? { height: placeholderHeight } : undefined}
    >
      <div
        ref={containerRef}
        className={classNames({
          [STYLES.StickySearchControls__fixed]: isSticky,
        })}
      >
        <div
          ref={innerRef}
          className={classNames(className, {
            [STYLES.StickySearchControls__inner]: isSticky,
          })}
          {...rest}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default StickySearchControls;
